/**
 * Validation Helper Utilities for Ant Design Form Integration with Yup
 *
 * These helpers provide seamless integration between Yup validation schemas
 * and Ant Design's Form validation system.
 */

/**
 * Validates a single field using Yup schema
 *
 * @param {Object} schema - Yup validation schema
 * @param {string} fieldName - Name of the field to validate
 * @param {any} value - Value to validate
 * @returns {Promise} - Resolves if valid, rejects with error message if invalid
 *
 * @example
 * <Form.Item
 *   name="email"
 *   rules={[
 *     {
 *       validator: (_, value) => validateField(loginSchema, 'email', value)
 *     }
 *   ]}
 * >
 *   <Input />
 * </Form.Item>
 */
export const validateField = async (schema, fieldName, value) => {
  try {
    await schema?.validateAt(fieldName, { [fieldName]: value });
    return Promise?.resolve();
  } catch (error) {
    return Promise?.reject(error.message);
  }
};

/**
 * Creates a validator function for Ant Design Form.Item rules
 * This is the recommended way to use Yup with Ant Design forms
 *
 * @param {Object} schema - Yup validation schema
 * @param {string} fieldName - Name of the field to validate
 * @returns {Function} - Validator function for Ant Design Form.Item
 *
 * @example
 * <Form.Item
 *   name="email"
 *   rules={[
 *     { validator: createYupValidator(loginSchema, 'email') }
 *   ]}
 * >
 *   <Input />
 * </Form.Item>
 */
export const createYupValidator = (schema, fieldName) => {
  return (_, value) => validateField(schema, fieldName, value);
};